// RoleFeaturesDialog.js - Dialog for assigning features to a role
import {
  useState,
  useEffect,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Stack,
  Typography,
  Divider,
  Box,
  Alert,
  CircularProgress,
  authFetch,
  authFetchRequest
} from './SettingsCommon';

const RoleFeaturesDialog = ({ open, role, onClose, onSave }) => {
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load all features and the role's current features whenever dialog opens
  useEffect(() => {
    if (!open || !role) return;
    setLoading(true);
    setError(null);
    Promise.all([
      authFetch('/user-roles/available-features'),
      authFetch(`/user-roles/${role.id}/features`)
    ])
      .then(([allFeatures, roleFeatures]) => {
        setAvailableFeatures(allFeatures || []);
        // roleFeatures is expected to be an array of feature codes
        setSelectedFeatures(Array.isArray(roleFeatures) ? roleFeatures : (roleFeatures?.features || []));
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading features:', err);
        setError('Failed to load features');
        setLoading(false);
      });
  }, [open, role]);

  const handleToggle = (code) => {
    setSelectedFeatures(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const handleSelectAll = () => {
    setSelectedFeatures(availableFeatures.filter(f => !f.coming_soon).map(f => f.code));
  };

  const handleSelectNone = () => {
    setSelectedFeatures([]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await authFetchRequest(`/user-roles/${role.id}/features`, { features: selectedFeatures }, 'PUT');
      onSave?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save features');
    } finally {
      setSaving(false);
    }
  };

  if (!role) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Manage Features for Role: <strong>{role.label}</strong>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Select which features users with this role can access. Features marked as "Coming Soon" are
          not yet available in the app regardless of this setting.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll}>Select All</Button>
              <Button size="small" variant="outlined" onClick={handleSelectNone}>Select None</Button>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
              <Stack spacing={1}>
                {availableFeatures.map((feature) => (
                  <FormControlLabel
                    key={feature.code}
                    control={
                      <Checkbox
                        checked={selectedFeatures.includes(feature.code)}
                        onChange={() => handleToggle(feature.code)}
                        disabled={feature.coming_soon}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ color: feature.coming_soon ? 'text.disabled' : 'inherit' }}
                        >
                          {feature.label}
                          {feature.coming_soon && (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
                              (Coming Soon)
                            </Typography>
                          )}
                        </Typography>
                        {feature.description && (
                          <Typography variant="caption" color="textSecondary">
                            {feature.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save Features
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleFeaturesDialog;
